import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Bookmark, MapPin, Users, Globe, Building2, TrendingUp, Layers, ExternalLink } from "lucide-react";
import { getCase, getRelated, getMeta, getCasePatterns } from "@/lib/apiClient";
import { CaseCard } from "@/components/CaseCards";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";

function Column({ title, children }) {
  return (
    <div className="border-t-2 border-ink pt-4">
      <h3 className="eyebrow text-ink/50 mb-3">{title}</h3>
      <div className="text-sm text-ink/80 leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [related, setRelated] = useState([]);
  const [casePatterns, setCasePatterns] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [notFound, setNotFound] = useState(false);
  const { market, isSaved, toggleSaved } = useSite();

  useEffect(() => {
    window.scrollTo(0, 0);
    setC(null); setNotFound(false); setCasePatterns([]);
    getCase(slug).then((data) => {
      setC(data);
      getRelated(slug).then(setRelated);
      getCasePatterns(slug).then(setCasePatterns).catch(() => {});
    }).catch(() => setNotFound(true));
    getMeta().then((m) => setDisclaimer(m.disclaimer));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display font-extrabold text-4xl">Case not found.</h1>
        <Link to="/" className="group inline-flex items-center gap-2 mt-6 font-bold">Back to Discover <ArrowRight className="arrow-move" size={18} /></Link>
      </div>
    );
  }
  if (!c) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 space-y-6">
        <div className="skeleton h-4 w-40" />
        <div className="skeleton h-16 w-full" />
        <div className="skeleton h-16 w-3/4" />
        <div className="skeleton aspect-[16/8] w-full" />
      </div>
    );
  }

  const saved = isSaved(c.slug);

  return (
    <article>
      {/* Header */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <div className="flex flex-wrap items-center gap-2 enter">
          {[c.industry, c.country, c.business_type].filter(Boolean).map((t) => (
            <span key={t} className="eyebrow bg-secondary text-ink px-2.5 py-1 border border-ink/15">{t}</span>
          ))}
        </div>
        <h1 data-testid="case-headline" className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl display-tight tracking-tight mt-5 enter" style={{ animationDelay: "60ms" }}>
          {c.headline}
        </h1>
        <p className="text-lg text-ink/70 mt-5 max-w-3xl leading-relaxed enter" style={{ animationDelay: "120ms" }}>{c.summary}</p>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-sm text-ink/70">
          <span className="inline-flex items-center gap-2 font-display font-bold text-ink text-base"><Building2 size={16} /> {c.company}</span>
          {c.country && <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {c.country}</span>}
          {c.company_size && <span className="inline-flex items-center gap-1.5"><Users size={15} /> {c.company_size}</span>}
          {c.region && <span className="inline-flex items-center gap-1.5"><Globe size={15} /> {c.region}</span>}
          <button
            data-testid="save-case-study-button"
            onClick={() => toggleSaved(c.slug)}
            className={`ml-auto btn-tactile inline-flex items-center gap-2 px-3 h-9 border-2 border-ink font-semibold text-xs ${saved ? "bg-ink text-bryellow" : "bg-white hover:bg-bryellow"}`}
          >
            <Bookmark size={14} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save"}
          </button>
        </div>
      </div>

      {/* Hero image + stat */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="grid md:grid-cols-5 gap-5 items-stretch">
          <div className="md:col-span-3 border-2 border-ink overflow-hidden enter">
            <img src={c.image} alt={c.company} className="w-full h-full object-cover aspect-[16/10]" />
          </div>
          {c.key_metric && (
            <Reveal className="md:col-span-2" >
              <div className="h-full bg-brlime border-2 border-ink p-6 flex flex-col justify-center">
                <div data-testid="reported-result-stat" className="font-display font-extrabold text-6xl lg:text-7xl display-tight tracking-tighter">{c.key_metric}</div>
                <div className="text-sm text-ink/70 mt-3 border-t-2 border-ink pt-3">{c.metric_context}</div>
              </div>
            </Reveal>
          )}
        </div>
      </div>

      {/* Four column breakdown */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
        <div data-testid="four-column-grid" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Column title="The Business">
            <p>{c.summary}</p>
            {c.company_size && <p className="text-ink/60">{c.company}{c.region ? ` · ${c.region}` : ""} · {c.company_size}.</p>}
          </Column>
          <Column title="Before AI"><p>{c.before_ai}</p></Column>
          <Column title="What Changed"><p>{c.what_changed}</p></Column>
          <Column title="The Result"><p>{c.result}</p></Column>
        </div>
      </div>

      {/* Why this matters + sidebar */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="grid lg:grid-cols-5 gap-6">
          <Reveal className="lg:col-span-3">
            <div data-testid="why-this-matters-panel" className="bg-bryellow border-2 border-ink p-7 sm:p-9 h-full">
              <p className="eyebrow text-ink/60">Why this matters</p>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl display-tight tracking-tight mt-3">
                {c.why_this_matters_headline || "The interesting part is the idea."}
              </h2>
              <div className="mt-5 space-y-4 text-ink/80 leading-relaxed">
                {(c.why_this_matters_body || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-2" delay={100}>
            <div className="border-2 border-ink p-6 h-full space-y-6">
              {(c.technology_provider || c.technology_product) && (
                <div>
                  <p className="eyebrow text-ink/50 mb-2">Technology Used</p>
                  <div className="font-display font-bold text-lg leading-tight">{c.technology_product || c.technology_provider}</div>
                  {c.technology_provider && c.technology_product && <div className="text-sm text-ink/60">by {c.technology_provider}</div>}
                  {c.technology_use?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-ink/50 mb-2">Used for:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {c.technology_use.map((u) => <span key={u} className="text-xs bg-secondary border border-ink/15 px-2 py-1">{u}</span>)}
                      </div>
                    </div>
                  )}
                  <div className="mt-4 space-y-1.5">
                    <a href={c.source_url} target="_blank" rel="noreferrer" className="group flex items-center gap-1 text-cobalt font-semibold text-sm">Visit technology provider <ArrowRight size={14} className="arrow-move" /></a>
                    <Link to={`/?tech=${encodeURIComponent(c.technology_provider)}`} className="group flex items-center gap-1 text-cobalt font-semibold text-sm">See other businesses using this <ArrowRight size={14} className="arrow-move" /></Link>
                  </div>
                </div>
              )}

              <div className="border-t-2 border-ink pt-5">
                <p className="eyebrow text-ink/50 mb-2">Source</p>
                <div className="font-bold text-sm">{c.source_organisation}</div>
                <div className="text-xs text-ink/50 mb-1">{c.source_type}</div>
                <div className="text-sm text-ink/80">{c.source_title}</div>
                {c.publication_date && <div className="text-xs text-ink/50 mt-1">Published: {c.publication_date}</div>}
                <a href={c.source_url} target="_blank" rel="noreferrer" data-testid="read-original-link" className="group inline-flex items-center gap-1 mt-3 font-bold text-sm text-ink border-b-2 border-brorange pb-0.5">
                  Read original case study <ArrowUpRight size={15} className="arrow-move" />
                </a>
                <p className="text-xs text-ink/50 mt-4 leading-relaxed italic">{disclaimer}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Explore opportunity CTA */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <Reveal>
          <div className="bg-cobalt text-white border-2 border-ink p-7 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="eyebrow text-white/70">Could this work in your market?</p>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl display-tight tracking-tight mt-2">
                Explore this opportunity in {market.name}.
              </h2>
            </div>
            <Link
              to={`/explore/${c.slug}`}
              data-testid="explore-opportunity-button"
              className="group btn-tactile shrink-0 inline-flex items-center gap-2 bg-white text-ink font-bold px-7 h-14 hover:bg-bryellow"
            >
              Explore Opportunity <ArrowRight size={18} className="arrow-move" />
            </Link>
          </div>
        </Reveal>
      </div>

      {/* Part of a larger pattern */}
      {casePatterns.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="case-patterns-section">
          <p className="eyebrow text-ink/50 mb-4">Part of a larger pattern</p>
          <div className="space-y-3">
            {casePatterns.map((p) => (
              <Link key={p.slug} to={`/pattern/${p.slug}`} data-testid={`case-pattern-${p.slug}`} className="group block border-2 border-ink p-5 bg-bryellow hover:shadow-[6px_6px_0_0_#0A0A0C]">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="font-display font-extrabold text-xl sm:text-2xl leading-tight group-hover:text-cobalt transition-colors">{p.name}</h3>
                    <p className="text-sm text-ink/70 mt-1">{p.supporting_count} similar transformations across {(p.industries || []).length} industries and {(p.countries || []).length} countries</p>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 font-bold text-sm">Explore Pattern <ArrowRight size={16} className="arrow-move" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display font-extrabold text-3xl tracking-tight uppercase">Related Cases</h2>
            <Link to="/" className="group hidden sm:inline-flex items-center gap-1 font-semibold text-sm">See more cases <ArrowRight size={15} className="arrow-move" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((r) => <CaseCard key={r.slug} c={r} />)}
          </div>
        </div>
      )}
    </article>
  );
}
